import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/constants/UserContext';
import { useToast } from '@/hooks/useToast';
import { uploadProfileImages } from '@/pages/Settings/settingsSaveHelpers';
import { logger } from '@/utils/logger';
import { getPostAuthRedirectOrDefault } from '@/utils/postAuthRedirect';
import { calculateAge } from '@/utils/age';
import { compressImageFile, compressFailureMessage } from '@/utils/compressImage';
import { snapshotPickedFile } from '@/utils/pickedFile';
import { MAX_CERTIFICATE_BYTES, MAX_CERTIFICATE_LABEL } from '@/constants/uploadLimits';

export interface PendingCertificate {
  id: string;
  title: string;
  date: string;
  file: File;
}


const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;
export type TshirtSize = (typeof SIZE_OPTIONS)[number] | '';

export function useOnboardingForm() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { user, updateUser } = useUser();
  const { showToast, hideToast, toast } = useToast();

  const userId = authUser?.id ?? user?.id ?? null;
  const role = (user?.role as string) ?? 'volunteer';
  const isOrganization = role === 'organization';

  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string>('');
  const profileInputRef = useRef<HTMLInputElement>(null);

  const [skills, setSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState('');

  const [dateOfBirth, setDateOfBirth] = useState('');

  const [about, setAbout] = useState('');

  const [certificates, setCertificates] = useState<PendingCertificate[]>([]);
  const [certificateTitle, setCertificateTitle] = useState('');
  const [certificateDate, setCertificateDate] = useState('');
  const [pendingCertFile, setPendingCertFile] = useState<File | null>(null);
  const certFileInputRef = useRef<HTMLInputElement>(null);

  const [tshirtSize, setTshirtSize] = useState<TshirtSize>('');

  const [processingImage, setProcessingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleProfileFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Phone cameras routinely produce 5-15MB photos. Downscale rather than
    // reject: an avatar needs a fraction of that.
    setProcessingImage(true);
    try {
      const result = await compressImageFile(file);
      if (!result.ok) {
        showToast(compressFailureMessage(result.reason), 'error');
        return;
      }
      setProfileFile(result.file);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result as string);
      reader.readAsDataURL(result.file);
    } finally {
      setProcessingImage(false);
    }
  };

  const clearProfileFile = () => {
    setProfileFile(null);
    setProfilePreview('');
    if (profileInputRef.current) profileInputRef.current.value = '';
  };

  const handleAddSkill = () => {
    const trimmed = newSkill.trim();
    if (!trimmed || skills.includes(trimmed)) return;
    setSkills((prev) => [...prev, trimmed]);
    setNewSkill('');
  };

  const handleDeleteSkill = (skill: string) => {
    setSkills((prev) => prev.filter((s) => s !== skill));
  };

  const handleCertFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdfMime = file.type === 'application/pdf';
    const isPdfExt = file.name.toLowerCase().endsWith('.pdf');
    if (!isPdfMime && !isPdfExt) {
      showToast('Only PDF files are allowed', 'error');
      return;
    }
    if (file.size > MAX_CERTIFICATE_BYTES) {
      showToast(`Certificate must be less than ${MAX_CERTIFICATE_LABEL}`, 'error');
      return;
    }
    // Read it now: this file sits in state until Finish, and by then Android may
    // have revoked the picker's read permission on the original reference.
    const snapshot = await snapshotPickedFile(file);
    if (!snapshot) {
      showToast('Could not read that file. Please pick it again.', 'error');
      return;
    }
    setPendingCertFile(snapshot);
  };

  const handleAddCertificate = () => {
    if (!certificateTitle.trim()) {
      showToast('Please enter a certificate title', 'error');
      return;
    }
    if (!certificateDate) {
      showToast('Please select a certificate date', 'error');
      return;
    }
    if (!pendingCertFile) {
      showToast('Please select a PDF file', 'error');
      return;
    }
    setCertificates((prev) => [
      ...prev,
      {
        id: `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        title: certificateTitle.trim(),
        date: certificateDate,
        file: pendingCertFile,
      },
    ]);
    setCertificateTitle('');
    setCertificateDate('');
    setPendingCertFile(null);
    if (certFileInputRef.current) certFileInputRef.current.value = '';
  };

  const handleRemoveCertificate = (id: string) => {
    setCertificates((prev) => prev.filter((c) => c.id !== id));
  };

  const finish = useCallback(async () => {
    if (!userId) return;
    setSubmitting(true);
    try {
      const pathPrefix = isOrganization ? `organizations/${userId}` : `volunteers/${userId}`;
      const { profileImage } = await uploadProfileImages(
        'volunteer-avatars',
        pathPrefix,
        profileFile,
        null,
        '',
        '',
        false,
      );

      const updates: Record<string, unknown> = {
        onboarding_completed: true,
      };
      if (profileImage) updates.profile_image = profileImage;
      if (skills.length > 0) updates.skills = skills;
      if (about.trim()) updates.description = about.trim();
      if (tshirtSize) updates.tshirtSize = tshirtSize;
      if (dateOfBirth) {
        updates.date_of_birth = dateOfBirth;
        // The column is numeric and nullable; only write a real number.
        const derivedAge = calculateAge(dateOfBirth);
        if (derivedAge !== null) updates.age = derivedAge;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId);
      if (profileError) throw profileError;

      const insertedCerts: Array<Record<string, unknown>> = [];
      for (const cert of certificates) {
        const path = `${userId}/certificate_${Date.now()}_${cert.id}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from('volunteer-certificates')
          .upload(path, cert.file, { contentType: 'application/pdf', upsert: true });
        if (uploadError) {
          logger.error('Onboarding cert upload failed', uploadError);
          continue;
        }
        const publicUrl = supabase.storage
          .from('volunteer-certificates')
          .getPublicUrl(path).data.publicUrl;
        const isoDate = new Date(cert.date).toISOString().split('T')[0];
        const { data: row, error: dbError } = await supabase
          .from('certificates')
          .insert({
            user_id: userId,
            title: cert.title,
            organization: 'Self-uploaded',
            date: isoDate,
            image_url: publicUrl,
            is_public: true,
          })
          .select()
          .single();
        if (dbError) {
          logger.error('Onboarding cert insert failed', dbError);
          continue;
        }
        if (row) insertedCerts.push(row as Record<string, unknown>);
      }

      updateUser({ ...updates });
      navigate(getPostAuthRedirectOrDefault(), { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save onboarding';
      logger.error('Onboarding finish error', err);
      showToast(message, 'error');
      setSubmitting(false);
    }
  }, [
    userId,
    isOrganization,
    profileFile,
    skills,
    about,
    dateOfBirth,
    tshirtSize,
    certificates,
    updateUser,
    navigate,
    showToast,
  ]);

  return {
    role,
    isOrganization,

    profileFile,
    profilePreview,
    profileInputRef,
    handleProfileFileChange,
    clearProfileFile,
    processingImage,

    skills,
    newSkill,
    setNewSkill,
    handleAddSkill,
    handleDeleteSkill,

    dateOfBirth,
    setDateOfBirth,

    about,
    setAbout,

    certificates,
    certificateTitle,
    setCertificateTitle,
    certificateDate,
    setCertificateDate,
    pendingCertFile,
    certFileInputRef,
    handleCertFileChange,
    handleAddCertificate,
    handleRemoveCertificate,

    tshirtSize,
    setTshirtSize,
    sizeOptions: SIZE_OPTIONS,

    submitting,
    finish,

    toast,
    hideToast,
    showToast,
  };
}

export type OnboardingFormReturn = ReturnType<typeof useOnboardingForm>;
